import {Button, Dialog, DialogTitle, Flex, Text} from '@priority-software/priority-style-react';
import {observer} from 'mobx-react';
import React, {forwardRef, useEffect, useRef} from 'react';
import {sendAnalyticsMonitor} from '../../../utils/monitoring';
import {helpPopoverRef} from '../../../_shared/Help/constants';
import {ChartType, monitorTransactionValues, Pages} from '../consts';
import {reportGeneratorsStore} from '../stores/ReportGeneratorsStore';
import {Report} from '../stores/ReportStore';
import texts from '../texts';
import {CloseWithHelp} from './CloseWithHelp';
import FormsSelect from './FormsSelect';
import styles from './styles.module.scss';
import {keys} from '../../../constants/KeyCodes';
import {createDataTestId} from '../../../utils/dataTestId';
import ChartTypeButtons from './Chart/ChartTypeButtons';

interface Props {
    onClose: () => void;
}

const Content = ({
    selected,
    onSelect,
    onCloseFormSelect,
    setCurrentType,
    currentType,
    setSelectedFormName
}) => (
    <Flex direction="column">
        <Flex
            direction="column"
            alignItems="flex-start"
            componentClasses={styles.formsSelectContainer}
        >
            <Text
                style={{marginBottom: '8px'}}
                skin="label-1"
                textColor="primary-700"
                wrap={false}
                {...createDataTestId('new-report-dlg-select-label', true)}
            >
                {texts.selectBaseForm}
            </Text>
            <FormsSelect
                value={selected}
                onSelect={onSelect}
                onClose={onCloseFormSelect}
                setSelectedFormName={setSelectedFormName}
            />
        </Flex>
        <ChartTypeButtons setCurrentType={setCurrentType} currentType={currentType} />
    </Flex>
);

const Footer = forwardRef((props: any, ref: any) => {
    const {onConfirm, onCancel, isEnabled} = props;

    return (
        <Flex componentClasses={styles.gap24} justifyContent="flex-end">
            <Button
                inputRef={!isEnabled ? ref : null}
                onClick={onCancel}
                skin="secondary"
                wideButton
                size="medium"
            >
                {texts.cancel}
            </Button>
            <Button
                inputRef={isEnabled ? ref : null}
                disabled={!isEnabled}
                onClick={onConfirm}
                wideButton
                size="medium"
            >
                {texts.ok}
            </Button>
        </Flex>
    );
});

const FormSelectDialog = observer(({onClose}: Props) => {
    const {
        setCurrentReport,
        setCurrentPage,
        setPrevPosition,
        currentType,
        setCurrentType,
        setSelectedForm,
        setSelectedFormNAme,
        selectedForm,
        selectedFormName,
        resetChartInfo,
        setCurrentChartTab
    } = reportGeneratorsStore?.currentScreen;
    const confirmButtonRef = useRef<HTMLButtonElement>();
    const onFormSelected = () => {
        const newReport = new Report(selectedForm);
        resetChartInfo();
        setCurrentChartTab('0');
        setCurrentReport(newReport);

        onClose();
        if (currentType === ChartType.report) {
            setPrevPosition(Pages.report);
            setCurrentPage(Pages.report);
        } else {
            setPrevPosition(Pages.chart);
            setCurrentPage(Pages.chart);
        }
    };

    const handleShowHelpMessage = e => {
        helpPopoverRef.current.showHelpPopover({content: texts.formSelectHelp, anchor: e.target});
        sendAnalyticsMonitor(monitorTransactionValues.AnalyticsNewHelp);
    };

    const handleConfirm = () => {
        if (!!selectedForm) {
            onFormSelected();
            sendAnalyticsMonitor(monitorTransactionValues.AnalyticsNewOK);
            sendAnalyticsMonitor(monitorTransactionValues.AnalyticsBaseForm, selectedFormName);
            sendAnalyticsMonitor(monitorTransactionValues.AnalyticsChart, currentType);
        }
    };

    const handleKeyDown = e => {
        if (e.key === keys.ENTER) {
            handleConfirm();
        }
    };

    const focusConfirmButton = () => {
        confirmButtonRef?.current?.focus();
    };
    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        focusConfirmButton();
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [selectedForm]);

    return (
        <Dialog
            width={640}
            isOpen={true}
            closeIcon={
                <CloseWithHelp
                    onHelp={handleShowHelpMessage}
                    componentClasses={styles.headerIcons}
                />
            }
            componentClasses={styles.dialog}
            darkMask
            onClose={() => {
                onClose();
                sendAnalyticsMonitor(monitorTransactionValues.AnalyticsNewX);
            }}
            content={
                <Content
                    onCloseFormSelect={focusConfirmButton}
                    selected={selectedForm}
                    onSelect={setSelectedForm}
                    setCurrentType={setCurrentType}
                    currentType={currentType}
                    setSelectedFormName={setSelectedFormNAme}
                />
            }
            title={<DialogTitle title={texts.newReport} textColor="primary-700" skin="h2" />}
            footer={
                <Footer
                    ref={confirmButtonRef}
                    isEnabled={!!selectedForm}
                    onConfirm={handleConfirm}
                    onCancel={() => {
                        onClose();
                        sendAnalyticsMonitor(monitorTransactionValues.AnalyticsNewCancel);
                    }}
                />
            }
        />
    );
});

export default FormSelectDialog;
